import { useEffect, useState } from 'react';
import { Outlet, createRootRoute, useMatchRoute } from '@tanstack/react-router';
import Sidebar from '@/components/layout/Sidebar.jsx';
import LoginPage from '@/pages/LoginPage.jsx';
import { useAuth } from '@/contexts/AuthContext.jsx';

/**
 * App shell: sidebar + <Outlet/> for whichever route matched. Also owns the
 * auth gate that used to live in App.jsx (unauthenticated users never see
 * the router-rendered shell) and the public-share bypass below.
 */
function RootComponent() {
  const { user, signOut } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const matchRoute = useMatchRoute();

  // Sign out on 401 — dispatched by the API client when auth fails.
  useEffect(() => {
    function onUnauthorized() { signOut(); }
    window.addEventListener('dg:unauthorized', onUnauthorized);
    return () => window.removeEventListener('dg:unauthorized', onUnauthorized);
  }, [signOut]);

  // Public share links render bare — no sidebar, no auth required — and
  // identically regardless of the viewer's own auth state, so a logged-in
  // user opening their own shared link sees the same thing an external
  // recipient would.
  const isSharedRoute = Boolean(matchRoute({ to: '/shared/$token' }));
  if (isSharedRoute) return <Outlet />;

  if (!user) return <LoginPage />;

  return (
    <div className={`app${sidebarCollapsed ? ' sidebar-collapsed' : ''}`}>
      <Sidebar user={user} onSignOut={signOut} collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed((c) => !c)} />
      <main className="main">
        <Outlet />
      </main>
    </div>
  );
}

export const Route = createRootRoute({ component: RootComponent });
